/**
 * Face-culled chunk mesher. Turns one 32x32x32 chunk of block ids into at most
 * two `BufferGeometry`s: opaque terrain and transparent water, split so water
 * can render with `depthWrite` off without dragging the terrain into the
 * transparency pass.
 *
 * A face is emitted only when the neighbour in that direction is not opaque
 * (with one extra rule: no faces between two water blocks, so a lake reads as
 * one volume, not a stack of sheets). Neighbour reads go through `getBlock`,
 * which resolves the edits overlay and generates the adjacent chunk on demand
 * at borders — the mesher never special-cases chunk edges.
 *
 * Air, torch and lamp emit nothing; every other solid block meshes as a full
 * cube. Per-face brightness is baked into vertex colours (top 1.0, x sides
 * 0.8, z sides 0.7, bottom 0.5) — baked AO and voxel light are M1.
 *
 * Runs once per chunk, so it builds plain number arrays and converts to typed
 * arrays at the end; nothing here is per-frame.
 */

import * as THREE from "three";
import { BLOCK, blockDef, isOpaque, isSolid, type AtlasTile } from "../data/blocks";
import { CHUNK_SIZE } from "../sim/chunk";
import { getBlock, type World } from "../sim/world";

export interface ChunkGeometry {
  opaque: THREE.BufferGeometry | null;
  transparent: THREE.BufferGeometry | null;
}

type FaceGroup = "top" | "side" | "bottom";

interface FaceDef {
  readonly dir: readonly [number, number, number];
  /**
   * Quad corners as [x, y, z, u, v] in block-local coordinates, wound CCW seen
   * from outside the cube. u/v are in tile space where v = 1 is the tile's
   * painted top edge.
   */
  readonly corners: ReadonlyArray<readonly [number, number, number, number, number]>;
  readonly shade: number;
  readonly group: FaceGroup;
}

const FACES: readonly FaceDef[] = [
  {
    dir: [0, 1, 0],
    corners: [
      [0, 1, 1, 0, 1],
      [1, 1, 1, 1, 1],
      [0, 1, 0, 0, 0],
      [1, 1, 0, 1, 0],
    ],
    shade: 1.0,
    group: "top",
  },
  {
    dir: [0, -1, 0],
    corners: [
      [1, 0, 1, 1, 0],
      [0, 0, 1, 0, 0],
      [1, 0, 0, 1, 1],
      [0, 0, 0, 0, 1],
    ],
    shade: 0.5,
    group: "bottom",
  },
  {
    dir: [1, 0, 0],
    corners: [
      [1, 1, 1, 0, 1],
      [1, 0, 1, 0, 0],
      [1, 1, 0, 1, 1],
      [1, 0, 0, 1, 0],
    ],
    shade: 0.8,
    group: "side",
  },
  {
    dir: [-1, 0, 0],
    corners: [
      [0, 1, 0, 0, 1],
      [0, 0, 0, 0, 0],
      [0, 1, 1, 1, 1],
      [0, 0, 1, 1, 0],
    ],
    shade: 0.8,
    group: "side",
  },
  {
    dir: [0, 0, 1],
    corners: [
      [0, 0, 1, 0, 0],
      [1, 0, 1, 1, 0],
      [0, 1, 1, 0, 1],
      [1, 1, 1, 1, 1],
    ],
    shade: 0.7,
    group: "side",
  },
  {
    dir: [0, 0, -1],
    corners: [
      [1, 0, 0, 0, 0],
      [0, 0, 0, 1, 0],
      [1, 1, 0, 0, 1],
      [0, 1, 0, 1, 1],
    ],
    shade: 0.7,
    group: "side",
  },
];

class GeometryBuilder {
  private readonly positions: number[] = [];
  private readonly normals: number[] = [];
  private readonly uvs: number[] = [];
  private readonly colors: number[] = [];
  private readonly indices: number[] = [];

  quad(face: FaceDef, wx: number, wy: number, wz: number, tile: AtlasTile, tileCount: number): void {
    const base = this.positions.length / 3;
    const u0 = tile.u / tileCount;
    const v0 = tile.v / tileCount;
    const span = 1 / tileCount;
    for (const corner of face.corners) {
      this.positions.push(wx + corner[0], wy + corner[1], wz + corner[2]);
      this.normals.push(face.dir[0], face.dir[1], face.dir[2]);
      // flipY is off, so the painted top edge of the tile sits at v0; a corner
      // with tile-space v = 1 maps there and v = 0 to the bottom edge.
      this.uvs.push(u0 + corner[3] * span, v0 + (1 - corner[4]) * span);
      this.colors.push(face.shade, face.shade, face.shade);
    }
    this.indices.push(base, base + 1, base + 2, base + 2, base + 1, base + 3);
  }

  build(): THREE.BufferGeometry | null {
    if (this.indices.length === 0) return null;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(this.positions), 3));
    geometry.setAttribute("normal", new THREE.BufferAttribute(new Float32Array(this.normals), 3));
    geometry.setAttribute("uv", new THREE.BufferAttribute(new Float32Array(this.uvs), 2));
    geometry.setAttribute("color", new THREE.BufferAttribute(new Float32Array(this.colors), 3));
    // A full chunk can exceed 65k vertices, so the index must be 32-bit.
    geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(this.indices), 1));
    return geometry;
  }
}

/**
 * Mesh one chunk. `cx/cy/cz` are chunk coordinates; `tileCount` is the atlas
 * grid size so tile coords become UVs. Positions are block-local (0..31); the
 * caller places the resulting mesh at the chunk's world origin.
 */
export function buildChunkGeometry(
  world: World,
  cx: number,
  cy: number,
  cz: number,
  tileCount: number,
): ChunkGeometry {
  const opaque = new GeometryBuilder();
  const transparent = new GeometryBuilder();
  const baseX = cx * CHUNK_SIZE;
  const baseY = cy * CHUNK_SIZE;
  const baseZ = cz * CHUNK_SIZE;

  // y outermost / x innermost matches the chunk byte layout (x fastest).
  for (let ly = 0; ly < CHUNK_SIZE; ly++) {
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const wx = baseX + lx;
        const wy = baseY + ly;
        const wz = baseZ + lz;
        const id = getBlock(world, wx, wy, wz);
        const water = id === BLOCK.WATER;
        // Air, torch and lamp are non-solid and emit no faces; everything
        // else (including non-opaque leaves) meshes as a full cube.
        if (!water && !isSolid(id)) continue;

        const out = water ? transparent : opaque;
        const def = blockDef(id);
        for (const face of FACES) {
          const neighbour = getBlock(world, wx + face.dir[0], wy + face.dir[1], wz + face.dir[2]);
          if (isOpaque(neighbour)) continue;
          if (water && neighbour === BLOCK.WATER) continue;
          out.quad(face, wx, wy, wz, def.tiles[face.group], tileCount);
        }
      }
    }
  }

  return { opaque: opaque.build(), transparent: transparent.build() };
}
